import React from 'react';

// Shared skeleton loaders (F7) — shimmer placeholders that replace every
// screen's bare "Loading…" text so a cold load reads as "content arriving",
// not "stalled". The shimmer + reduced-motion gate live in theme.css
// (`.ps-skeleton`); these are just the shapes. Mirrors the TodaySkeleton
// convention already used on the Today screen.

/** One shimmering block. Width/height accept any CSS length. */
export function SkeletonBlock({ w = '100%', h = 16, r, mb = 0, style }) {
  return (
    <div
      className="ps-skeleton"
      style={{
        width: w,
        height: h,
        ...(r != null ? { borderRadius: r } : null),
        marginBottom: mb,
        ...style,
      }}
    />
  );
}

/**
 * A card-shaped placeholder: optional banner image, a title line, then
 * `lines` body lines of decreasing width. Matches the `.ps-card` surfaces on
 * Profile / Question / Stats / Results.
 */
export function SkeletonCard({ image = false, lines = 2, style }) {
  return (
    <div className="ps-card" style={{ marginBottom: 14, ...style }}>
      {image && (
        <SkeletonBlock w="100%" h={0} style={{ aspectRatio: '4 / 3', marginBottom: 14 }} />
      )}
      <SkeletonBlock w="52%" h={20} mb={14} />
      {Array.from({ length: lines }).map((_, i) => (
        <SkeletonBlock
          key={i}
          w={i === lines - 1 ? '64%' : '92%'}
          h={14}
          mb={i === lines - 1 ? 0 : 9}
        />
      ))}
    </div>
  );
}

/**
 * A vertical list of row placeholders (leaderboard ranks, group members, coin
 * ledger entries). Each row is an optional leading avatar circle, a stack of
 * two text lines, and an optional trailing stat block — the shape shared by
 * every ranked/list surface on web.
 */
export function SkeletonRows({ rows = 6, avatar = true, trailing = true }) {
  return (
    <div>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 4px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          {avatar && <SkeletonBlock w={36} h={36} r="50%" />}
          <div style={{ flex: 1 }}>
            <SkeletonBlock w="42%" h={14} mb={7} />
            <SkeletonBlock w="26%" h={11} />
          </div>
          {trailing && <SkeletonBlock w={48} h={18} r={999} />}
        </div>
      ))}
    </div>
  );
}
